<script lang="ts" setup>
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import PerfectScrollbarOrigin from 'perfect-scrollbar'
import { useAppScrollBus } from '../@core/composable/useAppScroll'
import type { AppScrollPayload } from '../@core/composable/useAppScroll'
import { usePageLayoutStore } from '../stores/layout'
import { debounce } from 'perfect-debounce'
import { provide, ref, computed, watch, onMounted, nextTick, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useMobileInertiaScroll } from '../@core/composable/useMobileInertiaScroll'
const { isMobile } = useDevice()
const props = defineProps<{
  settings?: PerfectScrollbarOrigin.Options
  // 添加惯性滚动属性，默认为 true
  useInertia?: boolean
}>()

const emit = defineEmits<{
  (e: 'scroll', payload: AppScrollPayload): void
}>()

const scrollbarRef = ref<any>(null)
const lastTop = ref(0)
const lastLeft = ref(0)
const { emitScroll } = useAppScrollBus()
const pageLayoutStore = usePageLayoutStore()
const route = useRoute()

// 防抖函数，用于判断是否在顶部
const debouncedCheckIsAtTop = debounce((scrollTop: number) => {
  pageLayoutStore.setIsAtTop(scrollTop <= 0)
}, 50)

// 默认设置
const defaultSettings: PerfectScrollbarOrigin.Options = {
  wheelPropagation: false,
  suppressScrollX: true, // 禁用横向滚动
  // 添加惯性滚动相关配置
  swipeEasing: true,
  scrollingThreshold: 500,
  wheelSpeed: 2
}

// 滚动条设置
const scrollbarSettings = computed<PerfectScrollbarOrigin.Options>(() => ({
  ...defaultSettings,
  ...props.settings,
  // 根据 useInertia 属性控制惯性滚动
  swipeEasing: props.useInertia !== false
}))

// 监听路由变化，自动滚动到顶部
watch(() => route.fullPath, () => {
  // 使用 nextTick 确保在 DOM 更新完成后执行滚动操作
  nextTick(() => {
    scrollToTop()
  })
})

const handleScroll = (e?: Event) => {

  const el = (scrollbarRef.value?.$el ?? scrollbarRef.value) as HTMLElement | undefined
  const scrollTop = el?.scrollTop ?? 0
  const scrollLeft = el?.scrollLeft ?? 0
  const direction: AppScrollPayload['direction'] = scrollTop > lastTop.value ? 'down' :
    scrollTop < lastTop.value ? 'up' :
      scrollLeft > lastLeft.value ? 'right' :
        scrollLeft < lastLeft.value ? 'left' : 'none'

  lastTop.value = scrollTop
  lastLeft.value = scrollLeft

  // 保存滚动位置到 store
  pageLayoutStore.updateScrollTop(scrollTop)

  // 使用防抖函数检查是否在顶部
  debouncedCheckIsAtTop(scrollTop)

  const payload: AppScrollPayload = { scrollTop, scrollLeft, direction, event: e }
  emitScroll(payload)
  emit('scroll', payload)
}
const inertia = useMobileInertiaScroll({
  onScroll: () => {
    // 让你的原 handleScroll 照常触发
    handleScroll()
  }
})
// 滚动到顶部的方法
const scrollToTop = (immediate = false) => {
  const el = (scrollbarRef.value?.$el ?? scrollbarRef.value) as HTMLElement | undefined

  if (el) {
    // 如果 immediate 为 true，则立即滚动，否则使用 nextTick 确保 DOM 已更新
    if (immediate) {
      el.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      nextTick(() => {
        el.scrollTo({ top: 0, behavior: 'smooth' })
      })
    }

    pageLayoutStore.setIsAtTop(true)
  }
}

// 页面挂载完成后滚动到顶部
onMounted(() => {
  scrollToTop(true) // 立即执行，因为此时组件已经挂载

  // ⭐ 移动端启用
  if (isMobile) {
    const el = (scrollbarRef.value?.$el ?? scrollbarRef.value) as HTMLElement
    if (el) inertia.attach(el)
  }
})
const exposeShare = {
  scrollTo: (top: number, left: number = 0) => {
    const el = (scrollbarRef.value?.$el ?? scrollbarRef.value) as HTMLElement | undefined
    if (el) {
      el.scrollTop = top
      el.scrollLeft = left
    }
  },
  scrollToTop,
  getScrollTop: (): number => {
    const el = (scrollbarRef.value?.$el ?? scrollbarRef.value) as HTMLElement | undefined
    return el?.scrollTop ?? 0
  },
  getScrollLeft: (): number => {
    const el = (scrollbarRef.value?.$el ?? scrollbarRef.value) as HTMLElement | undefined
    return el?.scrollLeft ?? 0
  },
  // 添加获取是否在顶部的方法
  isAtTop: (): boolean => {
    const el = (scrollbarRef.value?.$el ?? scrollbarRef.value) as HTMLElement | undefined
    return (el?.scrollTop ?? 0) <= 0
  }
}

onUnmounted(() => {
  if (isMobile) {
    const el = (scrollbarRef.value?.$el ?? scrollbarRef.value) as HTMLElement
    if (el) inertia.detach(el)
  }
})
provide('pageScrollbar', exposeShare)
defineExpose(exposeShare)
</script>

<template>
  <PerfectScrollbar ref="scrollbarRef" class="page-scrollbar" :settings="scrollbarSettings" @ps-scroll-y="handleScroll"
    @ps-scroll-x="handleScroll">
    <slot />
  </PerfectScrollbar>
</template>

<style lang="scss">
.page-scrollbar {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
